import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, Search } from "lucide-react";
import { AccessCodeModal } from "@/components/AccessCodeModal";
import { useAccessCode } from "@/context/AccessCodeContext";
import { SearchOverlay } from "@/components/SearchOverlay";
import { coverUrl } from "@/lib/api";

const links = [
  { to: "/", label: "Inicio", testid: "nav-home-link" },
  { to: "/remixes", label: "Remixes", testid: "nav-remixes-link" },
  { to: "/mashups", label: "Mashups", testid: "nav-mashups-link" },
  { to: "/pool", label: "Pool", testid: "nav-pool-link" },
  { to: "/packs", label: "Packs", testid: "nav-packs-link" },
  { to: "/contact", label: "Contacto", testid: "nav-contact-link" },
];

export default function Navbar({ config }) {
  const { user, logout, login } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [codeOpen, setCodeOpen] = React.useState(false);
  const ac = useAccessCode();

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group min-w-0">
          {config?.logo_path ? (
            <img
              src={coverUrl(config.logo_path, 0)}
              alt="POMATA"
              className="h-9 w-auto object-contain"
            />
          ) : (
            <span className="font-display font-black text-lg tracking-tight">
              POMATA<span className="text-red-600">.</span>
            </span>
          )}
          <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.35em] text-white/40">
            In Session
          </span>
        </Link>

        <nav className="hidden md:flex items-center">
          {links.map((l, i) => {
            const active = loc.pathname === l.to;
            return (
              <React.Fragment key={l.to}>
                {i > 0 && <span className="h-4 w-px bg-white/15" aria-hidden="true" />}
                <Link to={l.to} data-testid={l.testid}
                  className={`px-3 py-2 font-display font-bold text-base uppercase tracking-normal transition-colors ${
                    active ? "text-white" : "text-white/60 hover:text-white"
                  }`}
                >
                  {l.label}
                  {active && <span className="block h-0.5 mt-1 bg-red-600" />}
                </Link>
              </React.Fragment>
            );
          })}
          <span className="h-4 w-px bg-white/15" aria-hidden="true" />
          <button type="button" onClick={() => setCodeOpen(true)} data-testid="nav-access-code-button" title={ac.code ? `Código activo: ${ac.code}` : "Código VIP"}
            className={`px-3 py-2 font-display font-bold text-base uppercase tracking-normal transition-colors ${ac.code ? "text-emerald-400" : "text-white/60 hover:text-white"}`}>
            VIP
            {ac.code && <span className="block h-0.5 mt-1 bg-emerald-500" />}
          </button>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <button type="button" onClick={() => setSearchOpen(true)} data-testid="nav-search-button" title="Buscar música"
            className="p-2 rounded-full text-white/60 hover:text-white hover:bg-white/5 transition-colors">
            <Search className="w-5 h-5" />
          </button>
          {user ? (
            <>
              <Link to="/perfil" data-testid="nav-profile-link"
                className="text-xs px-3 py-1.5 border border-white/15 text-white/70 rounded-full hover:border-red-600/50 hover:text-white font-mono uppercase tracking-wider">
                Mi música
              </Link>
              {user.is_admin && (
                <Link to="/admin" data-testid="nav-admin-link"
                  className="text-xs px-3 py-1.5 border border-red-600/40 text-red-500 rounded-full hover:bg-red-600/10 font-mono uppercase tracking-wider">
                  Admin
                </Link>
              )}
              <button data-testid="nav-logout-button" onClick={logout}
                className="text-xs text-white/60 hover:text-white font-mono uppercase tracking-wider">
                Salir
              </button>
            </>
          ) : (
            <button data-testid="admin-login-button"
              onClick={() => login()}
              className="text-xs px-4 py-2 border border-white/10 text-white/70 rounded-full hover:border-red-600/60 hover:text-white font-mono uppercase tracking-wider">
              Entrar
            </button>
          )}
        </div>

        <div className="md:hidden flex items-center">
          <button type="button" onClick={() => setSearchOpen(true)} data-testid="nav-search-button-mobile" className="text-white/70 hover:text-white p-2"><Search className="w-5 h-5" /></button>
          <button type="button" onClick={() => setCodeOpen(true)} data-testid="nav-access-code-button-mobile" className={`px-2 py-2 font-display font-bold text-sm uppercase ${ac.code ? "text-emerald-400" : "text-white/70 hover:text-white"}`}>VIP</button>
          <button data-testid="nav-mobile-toggle" className="text-white p-2" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <AccessCodeModal open={codeOpen} onClose={() => setCodeOpen(false)} />

      {open && (
        <div className="md:hidden border-t border-white/5 bg-black/95">
          <div className="px-5 py-4 flex flex-col gap-2">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                data-testid={`${l.testid}-mobile`}
                className="text-white/70 hover:text-white py-2 font-display font-bold text-sm uppercase tracking-wider">
                {l.label}
              </Link>
            ))}
            {!user && (
              <button onClick={() => login()} data-testid="nav-login-button-mobile"
                className="text-red-500 py-2 text-left font-mono uppercase text-xs tracking-wider">
                Entrar
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
